import { Link, useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { useClerk } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Menu, Truck, LogOut, LayoutDashboard, FileEdit, ClipboardList, Tag, Activity, ArrowLeft, Columns, Users, BugPlay, Bot, Plug, ChevronDown } from "lucide-react";
import { useListDrafts } from "@workspace/api-client-react";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DnaLogo } from "@/components/DnaLogo";
import { clearStaffPreviewMode, setStaffPreviewMode } from "@/lib/staff";

export default function StaffLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { data: pendingDrafts } = useListDrafts({ status: "pending" });
  const pendingDraftCount = pendingDrafts?.length ?? 0;

  useEffect(() => {
    clearStaffPreviewMode();
  }, [location]);

  const handleBackToPublic = () => {
    setStaffPreviewMode();
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const handleSignOut = () => {
    clearStaffPreviewMode();
    signOut({ redirectUrl: "/" });
  };

  type NavItem = {
    href: string;
    label: string;
    icon: typeof LayoutDashboard;
    testId: string;
    count?: number;
  };

  const dailyItems: NavItem[] = [
    { href: "/staff", label: "Reports", icon: LayoutDashboard, testId: "staff-nav-dashboard" },
    { href: "/staff/report", label: "Log a report", icon: BugPlay, testId: "staff-nav-report" },
    { href: "/staff/issues", label: "Public board", icon: Columns, testId: "staff-nav-issues" },
    { href: "/staff/drafts", label: "Drafts", icon: Bot, testId: "staff-nav-drafts", count: pendingDraftCount },
  ];

  const secondaryGroups: { label: string; items: NavItem[] }[] = [
    {
      label: "Content",
      items: [
        { href: "/staff/whats-changed", label: "What's Changed", icon: FileEdit, testId: "staff-nav-whats-changed" },
        { href: "/staff/categories", label: "Categories", icon: Tag, testId: "staff-nav-categories" },
        { href: "/staff/statuses", label: "Statuses", icon: Activity, testId: "staff-nav-statuses" },
      ],
    },
    {
      label: "Administration",
      items: [
        { href: "/staff/users", label: "Users", icon: Users, testId: "staff-nav-users" },
        { href: "/staff/mcp-connections", label: "MCP connections", icon: Plug, testId: "staff-nav-mcp" },
      ],
    },
    {
      label: "Activity",
      items: [
        { href: "/staff/audit-log", label: "Audit Log", icon: ClipboardList, testId: "staff-nav-audit-log" },
      ],
    },
  ];

  const secondaryActive = secondaryGroups.some((g) => g.items.some((it) => it.href === location));

  const closeMobile = () => setIsMobileMenuOpen(false);

  const DraftCount = ({ count }: { count?: number }) =>
    count && count > 0 ? (
      <span
        className="rounded-full bg-primary text-primary-foreground text-xs px-1.5 min-w-5 text-center"
        data-testid="staff-nav-drafts-count"
        aria-label={`${count} pending`}
      >
        {count}
      </span>
    ) : null;

  const NavItemLink = ({ item, mobile }: { item: NavItem; mobile: boolean }) => {
    const Icon = item.icon;
    const active = location === item.href;
    return (
      <Link
        href={item.href}
        aria-current={active ? "page" : undefined}
        className={`text-sm font-medium transition-colors hover:text-primary flex items-center gap-2 ${active ? "text-primary" : "text-slate-600 dark:text-slate-300"}`}
        onClick={() => mobile && closeMobile()}
      >
        <Icon className="w-4 h-4" aria-hidden="true" />
        <span data-testid={item.testId}>{item.label}</span>
        <DraftCount count={item.count} />
      </Link>
    );
  };

  const UtilityLinks = ({ mobile }: { mobile: boolean }) => (
    <>
      <Link
        href="/"
        className="text-sm font-medium transition-colors hover:text-primary flex items-center gap-2 text-slate-500 dark:text-slate-400"
        onClick={() => {
          handleBackToPublic();
          if (mobile) closeMobile();
        }}
        data-testid="staff-nav-back-to-public"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        <span>Back to public site</span>
      </Link>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleSignOut}
        className="justify-start px-0 text-slate-600 dark:text-slate-300 hover:text-destructive hover:bg-transparent"
        data-testid="staff-nav-sign-out"
      >
        <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
        Sign Out
      </Button>
    </>
  );

  const DesktopNav = () => (
    <div className="flex items-center space-x-6">
      {dailyItems.map((item) => (
        <NavItemLink key={item.href} item={item} mobile={false} />
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger
          className={`text-sm font-medium transition-colors hover:text-primary flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${secondaryActive ? "text-primary" : "text-slate-600 dark:text-slate-300"}`}
          data-testid="staff-nav-manage"
        >
          Manage <ChevronDown className="w-4 h-4" aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {secondaryGroups.map((group, gi) => (
            <DropdownMenuGroup key={group.label}>
              {gi > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {group.label}
              </DropdownMenuLabel>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link
                      href={item.href}
                      aria-current={location === item.href ? "page" : undefined}
                      className={`flex items-center gap-2 cursor-pointer ${location === item.href ? "text-primary" : ""}`}
                    >
                      <Icon className="w-4 h-4" aria-hidden="true" />
                      <span data-testid={item.testId}>{item.label}</span>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="w-px h-6 bg-slate-200 dark:bg-slate-800 mx-2" />
      <UtilityLinks mobile={false} />
    </div>
  );

  const MobileNav = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col space-y-4" role="group" aria-label="Daily work">
        {dailyItems.map((item) => (
          <NavItemLink key={item.href} item={item} mobile />
        ))}
      </div>
      {secondaryGroups.map((group) => (
        <div key={group.label} className="flex flex-col space-y-3" role="group" aria-label={group.label}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{group.label}</p>
          {group.items.map((item) => (
            <NavItemLink key={item.href} item={item} mobile />
          ))}
        </div>
      ))}
      <div className="w-full h-px bg-slate-200 dark:bg-slate-800" />
      <div className="flex flex-col space-y-4">
        <UtilityLinks mobile />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Staff · MOTUS Bug Board</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link
              href="/"
              className="flex items-center gap-2 group"
              aria-label="MOTUS Bug Board home"
            >
              <div className="bg-primary text-primary-foreground p-1.5 rounded-lg group-hover:bg-orange-600 transition-colors">
                <Truck className="w-5 h-5" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">MOTUS Bug Board</span>
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Staff Panel</span>
              </div>
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center" aria-label="Staff">
            <DesktopNav />
          </nav>

          {/* Mobile Nav */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="xl:hidden">
              <Button variant="ghost" size="icon" className="text-slate-600" data-testid="staff-mobile-menu-trigger">
                <Menu className="w-6 h-6" />
                <span className="sr-only">Toggle Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[340px] overflow-y-auto">
              <SheetTitle className="sr-only">Staff navigation</SheetTitle>
              <SheetDescription className="sr-only">Daily work, content, administration, and activity.</SheetDescription>
              <div className="flex flex-col gap-8 mt-6">
                <div className="flex items-center gap-2">
                  <div className="bg-primary text-primary-foreground p-1.5 rounded-lg">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">MOTUS Bug Board</span>
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Staff Panel</span>
                  </div>
                </div>
                <nav aria-label="Staff">
                  <MobileNav />
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
        {children}
      </main>

      <footer className="border-t bg-white dark:bg-slate-900 py-6 md:py-8 mt-auto">
        <div className="container mx-auto px-4 text-center md:text-left flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col items-center gap-2 md:items-start">
            <a
              href="https://donnormanassociates.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DnaLogo className="h-5 w-auto" width={96} height={20} loading="lazy" />
            </a>
            <p className="text-sm text-slate-500">
              MOTUS Bug Board &copy; 2026. Not affiliated with the FMCSA.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Link
              href="/"
              onClick={handleBackToPublic}
              className="text-sm text-slate-500 hover:text-primary transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to public site
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
